import React from 'react';
import PropTypes from 'prop-types';
import { FaSpinner } from 'react-icons/fa';

/**
 * IconButton — a tw- button that pairs an icon with optional label text.
 *
 * Usage:
 *   <IconButton icon={<FaArrowLeft />} onClick={...} />
 *   <IconButton icon={<FaPlus />} iconAlign="left" size="sm" variant="primary">New</IconButton>
 *   <IconButton icon={<FaSync />} iconAlign="right" variant="ghost">Refresh</IconButton>
 */
const IconButton = ({
  icon,
  iconAlign = 'left',
  children,
  type = 'button',
  variant = 'ghost',
  size = 'sm',
  disabled = false,
  loading = false,
  className = '',
  onClick,
  title,
  ...props
}) => {
  const base = 'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-font-medium tw-rounded-md tw-transition-colors tw-cursor-pointer tw-border tw-outline-none';

  const variantClasses = {
    primary:   'tw-bg-blue-600 tw-text-white tw-border-blue-600 hover:tw-bg-blue-700 hover:tw-border-blue-700 disabled:tw-opacity-50',
    secondary: 'tw-bg-slate-200 tw-text-slate-800 tw-border-slate-200 hover:tw-bg-slate-300 disabled:tw-opacity-50',
    danger:    'tw-bg-red-600 tw-text-white tw-border-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50',
    ghost:     'tw-bg-white tw-text-slate-700 tw-border-slate-300 hover:tw-bg-slate-50 disabled:tw-opacity-50',
    success:   'tw-bg-green-600 tw-text-white tw-border-green-600 hover:tw-bg-green-700 disabled:tw-opacity-50',
    warning:   'tw-bg-yellow-500 tw-text-white tw-border-yellow-500 hover:tw-bg-yellow-600 disabled:tw-opacity-50',
  };

  const sizeClasses = {
    xs: 'tw-px-1.5 tw-py-1 tw-text-xs',
    sm: 'tw-px-2.5 tw-py-1.5 tw-text-xs',
    md: 'tw-px-3 tw-py-2 tw-text-sm',
    lg: 'tw-px-4 tw-py-2.5 tw-text-base',
  };

  const finalClass = [
    base,
    variantClasses[variant] ?? variantClasses.ghost,
    sizeClasses[size] ?? sizeClasses.sm,
    disabled || loading ? 'tw-cursor-not-allowed' : '',
    className,
  ].join(' ');

  const spinnerIcon = loading ? <FaSpinner className="tw-animate-spin" size={12} /> : null;
  const renderedIcon = spinnerIcon ?? icon;

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={finalClass}
      title={title}
      {...props}
    >
      {iconAlign === 'right' && children}
      {renderedIcon}
      {(iconAlign === 'left' || iconAlign === 'middle') && children}
    </button>
  );
};

IconButton.propTypes = {
  icon:      PropTypes.node.isRequired,
  iconAlign: PropTypes.oneOf(['left', 'right', 'middle']),
  children:  PropTypes.node,
  type:      PropTypes.oneOf(['button', 'submit', 'reset']),
  variant:   PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost', 'success', 'warning']),
  size:      PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  disabled:  PropTypes.bool,
  loading:   PropTypes.bool,
  className: PropTypes.string,
  onClick:   PropTypes.func,
  title:     PropTypes.string,
};

export default IconButton;
